import { useEffect, useRef, type RefObject } from 'react'
import { cn } from '../../lib/utils'
import { StorageDrawer } from '../storage/storage-drawer'
import { MainPanel } from './main-panel'
import { MetadataPanel } from './metadata-panel'
import { Sidebar } from './sidebar'

const DRAWER_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface ResponsiveEditorDrawerLayoutProps {
  theme: 'dark' | 'light'
  storageEnabled: boolean
  documentNavigationOpen: boolean
  metadataOpen: boolean
  setDocumentNavigationOpen: (open: boolean) => void
  setMetadataOpen: (open: boolean) => void
  canCreate: boolean
}

function useDrawerFocus(
  open: boolean,
  drawerRef: RefObject<HTMLDivElement | null>,
  triggerLabel: string,
  setOpen: (open: boolean) => void,
) {
  useEffect(() => {
    if (!open) return
    const drawer = drawerRef.current
    const trigger = document.querySelector<HTMLElement>(`[aria-label="${triggerLabel}"]`)
    const focusable = Array.from(drawer?.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE) ?? [])
    focusable[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        return
      }
      if (event.key !== 'Tab' || focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      trigger?.focus()
    }
  }, [drawerRef, open, setOpen, triggerLabel])
}

export function ResponsiveEditorDrawerLayout({
  theme,
  storageEnabled,
  documentNavigationOpen,
  metadataOpen,
  setDocumentNavigationOpen,
  setMetadataOpen,
  canCreate,
}: ResponsiveEditorDrawerLayoutProps) {
  const documentDrawerRef = useRef<HTMLDivElement>(null)
  const metadataDrawerRef = useRef<HTMLDivElement>(null)
  const anyDrawerOpen = documentNavigationOpen || metadataOpen

  useDrawerFocus(
    documentNavigationOpen,
    documentDrawerRef,
    'Open document navigation',
    setDocumentNavigationOpen,
  )
  useDrawerFocus(metadataOpen, metadataDrawerRef, 'Open document properties', setMetadataOpen)

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden font-sans antialiased',
        theme === 'dark' ? 'bg-surface-0 text-neutral-100' : 'bg-neutral-50 text-neutral-900',
      )}
    >
      <div
        className="flex min-h-0 flex-1"
        aria-hidden={anyDrawerOpen || undefined}
        inert={anyDrawerOpen ? true : undefined}
      >
        <MainPanel />
      </div>

      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm drawer-backdrop',
          anyDrawerOpen && 'drawer-open',
        )}
        onClick={() => {
          setDocumentNavigationOpen(false)
          setMetadataOpen(false)
        }}
        aria-hidden="true"
      />

      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 h-full w-[280px] max-w-[85vw] drawer-left',
          documentNavigationOpen && 'drawer-open',
        )}
        aria-hidden={!documentNavigationOpen}
        aria-label="Document navigation drawer"
        aria-modal="true"
        data-document-navigation-drawer="true"
        inert={!documentNavigationOpen ? true : undefined}
        ref={documentDrawerRef}
        role="dialog"
      >
        <Sidebar canCreate={canCreate} />
      </div>

      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 h-full w-[300px] max-w-[85vw] drawer-right',
          metadataOpen && 'drawer-open',
        )}
        aria-hidden={!metadataOpen}
        inert={!metadataOpen ? true : undefined}
        role="dialog"
        aria-modal="true"
        aria-label="Document properties drawer"
        ref={metadataDrawerRef}
      >
        <MetadataPanel />
      </div>

      {storageEnabled && <StorageDrawer />}
    </div>
  )
}
